"use client";

import {
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import Link from "next/link";
import { useAuth, useSession } from "@clerk/nextjs";
import { useRouter } from "@bprogress/next/app";
import { LoaderCircle, LogIn, PartyPopper, Ticket } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import {
  useStandaloneDisplay,
  type FeaturedAwardShow,
} from "@award-party/shared";
import { getSignInHref } from "@/lib/auth-redirect";
import {
  NATIVE_LAUNCH_VARIANT_IDS,
  formatNativeLaunchShowDate,
  resolveNativeLaunchVariant,
  type NativeLaunchVariantId,
} from "@/lib/native-launch-home";
import { BrandMark } from "@/components/brand-mark";
import { PartiesListSkeleton } from "@/components/marketing/parties-list-skeleton";
import {
  NATIVE_PARTIES_REDIRECT_FALLBACK_MS,
  recoverStuckNativePartiesRedirect,
} from "@/lib/native-parties-redirect";

declare global {
  interface Window {
    AwardPartyNative?: unknown;
  }
}

type NativeSimpleHomeProps = {
  featuredShow: FeaturedAwardShow | null;
  children: ReactNode;
};

const NATIVE_READY_EVENT = "award-party:native-ready";

function subscribeToNativeShell(onStoreChange: () => void) {
  window.addEventListener(NATIVE_READY_EVENT, onStoreChange);
  return () => {
    window.removeEventListener(NATIVE_READY_EVENT, onStoreChange);
  };
}

function readNativeShell(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    typeof window.AwardPartyNative !== "undefined" ||
    document.documentElement.dataset.awardPartyNativeShell === "true"
  );
}

export function NativeSimpleHome({
  featuredShow,
  children,
}: NativeSimpleHomeProps) {
  const isNativeHome = useNativeHomeExperience();

  if (!isNativeHome) {
    return children;
  }

  return <NativeSimpleHomeContent featuredShow={featuredShow} />;
}

export function useNativeHomeExperience(): boolean {
  const standalone = useStandaloneDisplay();
  const isNativeShell = useSyncExternalStore(
    subscribeToNativeShell,
    readNativeShell,
    () => false,
  );

  return isNativeShell || standalone;
}

function NativeSimpleHomeContent({
  featuredShow,
}: Pick<NativeSimpleHomeProps, "featuredShow">) {
  const { isLoaded, isSignedIn } = useAuth();
  const { isLoaded: sessionLoaded, session } = useSession();
  const sessionPending = session?.status === "pending";

  if (!isLoaded || !sessionLoaded || sessionPending) {
    return <NativeHomeLoading messageKey="loadingAccount" />;
  }

  if (isSignedIn) {
    return <NativePartyRedirect />;
  }

  return <NativeSignedOutHome featuredShow={featuredShow} />;
}

function NativePartyRedirect() {
  const router = useRouter();

  useEffect(() => {
    router.replace("/parties");
    const timer = window.setTimeout(() => {
      const recovery = recoverStuckNativePartiesRedirect(
        window.location.pathname,
      );
      if (recovery === "reload") {
        window.location.reload();
        return;
      }
      window.location.replace("/parties");
    }, NATIVE_PARTIES_REDIRECT_FALLBACK_MS);
    return () => window.clearTimeout(timer);
  }, [router]);

  return <PartiesListSkeleton />;
}

function NativeHomeLoading({
  messageKey,
}: {
  messageKey: "loadingAccount";
}) {
  const t = useTranslations("NativeHome");

  return (
    <main
      className="flex min-h-0 flex-1 items-center justify-center px-6"
      aria-busy="true"
    >
      <div className="flex flex-col items-center gap-3 text-center">
        <LoaderCircle
          className="size-5 animate-spin text-(--mk-gold)"
          aria-hidden="true"
        />
        <p className="text-sm text-(--mk-cream-60)">{t(messageKey)}</p>
      </div>
    </main>
  );
}

function NativeSignedOutHome({
  featuredShow,
}: Pick<NativeSimpleHomeProps, "featuredShow">) {
  const t = useTranslations("NativeHome");
  const locale = useLocale();
  const [variant] = useState<NativeLaunchVariantId>(() =>
    resolveNativeLaunchVariant(
      typeof sessionStorage === "undefined" ? null : sessionStorage,
    ),
  );
  const copyKey = `variants.${variant}` as const;

  return (
    <main className="relative flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain px-6 pb-[max(2rem,max(env(safe-area-inset-bottom,0px),var(--safe-area-inset-bottom,0px)))] md:justify-center md:px-10">
      <header className="relative z-10 flex shrink-0 flex-col items-center gap-4 pt-6 md:pt-0 [@media(max-height:700px)]:gap-2 [@media(max-height:700px)]:pt-3">
        <div className="flex flex-col items-center gap-2.5">
          <BrandMark size={28} priority className="h-7 w-7 md:h-10 md:w-10" />
          <p className="text-[10px] font-medium tracking-[0.34em] text-(--mk-cream-60) uppercase md:text-xs">
            {t("brand")}
          </p>
        </div>
        <div className="flex items-center gap-1.5" aria-hidden="true">
          {NATIVE_LAUNCH_VARIANT_IDS.map((id) => (
            <span
              key={id}
              className={`size-1.5 rounded-full ${
                id === variant ? "bg-(--mk-gold)" : "bg-(--mk-cream-20)"
              }`}
            />
          ))}
        </div>
      </header>

      <section className="relative z-10 flex flex-1 flex-col items-center justify-center py-2 text-center md:flex-none md:py-12 [@media(max-height:700px)]:py-1">
        <p className="mb-6 rounded-full border border-(--mk-cream-20) px-3.5 py-1 text-[11px] tracking-[0.06em] text-(--mk-cream-60) md:text-sm [@media(max-height:700px)]:mb-3">
          {launchBadgeLabel(variant, featuredShow, locale, t)}
        </p>
        <h1 className="max-w-[14ch] font-serif text-[3.25rem] leading-[0.92] font-medium tracking-[-0.03em] text-(--mk-cream) sm:text-6xl md:max-w-[16ch] md:text-8xl [@media(max-height:700px)]:text-[2.75rem] [@media(max-height:700px)]:leading-[0.95]">
          {t.rich(`${copyKey}.headline`, {
            em: (chunks) => (
              <em className="font-serif italic text-(--mk-gold)">{chunks}</em>
            ),
            br: () => <br />,
          })}
        </h1>
        <p className="mt-5 max-w-[20rem] text-[15px] leading-6 text-(--mk-cream-60) md:mt-6 md:max-w-lg md:text-xl md:leading-8 [@media(max-height:700px)]:mt-3">
          {t(`${copyKey}.body`)}
        </p>
      </section>

      <div className="relative z-10 mx-auto flex w-full max-w-sm shrink-0 flex-col items-center md:mt-4 md:max-w-lg">
        <Link
          href="/parties/create"
          className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-(--mk-gold) px-5 py-4 text-base font-semibold text-(--mk-ink) no-underline transition-colors hover:bg-(--mk-gold-light) md:py-5 md:text-lg"
        >
          <PartyPopper className="size-4" aria-hidden="true" />
          {t(`${copyKey}.cta`)}
        </Link>
        <Link
          href={getSignInHref()}
          className="mt-3 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-full border border-(--mk-cream-20) bg-transparent px-5 py-3.5 text-base font-semibold text-(--mk-cream) no-underline transition-colors hover:border-(--mk-cream-35) hover:bg-(--mk-cream-04) md:py-5 md:text-lg"
        >
          <LogIn className="size-4" aria-hidden="true" />
          {t(`${copyKey}.signIn`)}
        </Link>
        <Link
          href="/join"
          aria-label={t("inviteCode")}
          className="mt-7 inline-flex cursor-pointer items-center gap-1.5 text-sm text-(--mk-cream-60) underline decoration-(--mk-cream-20) underline-offset-4 [@media(max-height:700px)]:mt-4"
        >
          <Ticket className="size-3.5" aria-hidden="true" />
          {t(`${copyKey}.invite`)}
        </Link>
        <div className="mt-8 h-px w-14 bg-(--mk-cream-20) [@media(max-height:700px)]:mt-4" />
        <p className="mt-4 text-center text-[11px] tracking-[0.04em] text-(--mk-cream-35) [@media(max-height:700px)]:mt-2">
          {t(`${copyKey}.footer`)}
        </p>
      </div>
    </main>
  );
}

function launchBadgeLabel(
  variant: NativeLaunchVariantId,
  featuredShow: FeaturedAwardShow | null,
  locale: string,
  t: ReturnType<typeof useTranslations<"NativeHome">>,
): string {
  const showLabel = featuredShow
    ? `${featuredShow.displayName} ${featuredShow.year}`
    : "";
  const date = featuredShow
    ? formatNativeLaunchShowDate(featuredShow.startTime, locale)
    : "";
  const status = featuredShow?.isLive ? t("showLive") : t("picksOpen");

  if (variant === "competitive") {
    return showLabel
      ? t("variants.competitive.badge", { show: showLabel, status })
      : t("picksOpen");
  }

  if (variant === "brand") {
    if (showLabel && date) {
      return t("variants.brand.badge", { show: showLabel, date });
    }
    return showLabel || t("badgeFallback");
  }

  return showLabel
    ? t("variants.social.badge", {
        prefix: t("variants.social.badgePrefix"),
        show: showLabel,
      })
    : t("variants.social.badgePrefix");
}
